import { useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { snippetOf, type ChatMessage } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { RichText } from './CustomEmoji';

/**
 * What the room decided was worth keeping, collapsed to one line.
 *
 * A room holds a thousand messages and then starts deleting the oldest, which
 * means the good answer somebody wrote on a Tuesday is gone by Thursday. A pin
 * is the one exemption from that window, so this bar is the only thing in the
 * panel that accumulates: a level room that has been open for a month is worth
 * walking into because of what is behind this button, and a room with nothing
 * pinned shows no bar at all.
 *
 * Collapsed by default, and that is not a space-saving compromise. The
 * conversation is what the panel is for; a list of twenty pinned messages
 * standing permanently on top of a 240px-tall panel would leave three lines
 * for the room. So it is one line saying how many, and it opens when somebody
 * wants them.
 *
 * Each entry is a button that goes to the message rather than a copy of it.
 * A pin is a pointer into the conversation — the reply under it, the question
 * above it and who said it are usually half the answer — and a strip that
 * showed the text in full would be a second, worse message list that people
 * read instead of the room.
 *
 * The text is cut with the protocol's own `snippetOf`, which is what the
 * server uses for a reply quote, so a line that is one line here is one line
 * everywhere. It is still rendered through `RichText`: a pinned message is a
 * message, and the twenty-eight emoji this product drew for itself are drawn
 * here too. Nothing else in it becomes markup.
 *
 * Escape collapses this and stops there. It is not one of the header's
 * popovers — it overlays nothing and holds no draft — so it does not belong in
 * `ChatPanel`'s one-at-a-time policy; what it does belong to is the same rule
 * every other nested control in the panel follows: the innermost open thing
 * takes the first Escape, and the panel takes the next one.
 */
export interface PinnedStripProps {
  pins: ChatMessage[];
  /** Go to one, in the room it was pinned in. */
  onOpen(id: number): void;
}

export function PinnedStrip({ pins, onOpen }: PinnedStripProps) {
  const s = useStrings().chat;
  const [open, setOpen] = useState(false);

  if (pins.length === 0) return null;

  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape' || !open) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <div className="chat-pins" onKeyDown={onKeyDown}>
      <button
        type="button"
        className="disclose chat-pins-btn"
        aria-expanded={open}
        aria-controls="chat-pins-list"
        title={open ? s.pins.hide : s.pins.show}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="disclose-mark" aria-hidden="true" />
        <span className="label">{s.pins.count(pins.length)}</span>
      </button>

      {open && (
        <ul id="chat-pins-list" className="chat-pins-list scroll" aria-label={s.pins.label}>
          {pins.map((p) => (
            <li key={p.id} className="chat-pin">
              <button type="button" className="chat-pin-open" title={s.pins.open} onClick={() => onOpen(p.id)}>
                {/* A name is the person's own and may be in either script, so
                    it takes its direction from itself rather than from the
                    panel — and so does the line under it, which is theirs
                    too. */}
                <span className="msg-name" dir="auto">
                  {p.name}
                </span>
                <span className="chat-pin-text" dir="auto">
                  {p.hidden ? (
                    s.list.removed
                  ) : p.kind === 'cleared' ? (
                    s.list.cleared(p.level ?? 0)
                  ) : (
                    <RichText text={snippetOf(p.text)} />
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PinnedStrip;
